import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/core/services/user.service';
import { AuthService } from '@auth0/auth0-angular';
import { take } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { AVAILABLE_LANGUAGES } from 'src/app/core/constants/language.constants';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { isLoginRequiredError, isInteractiveReauthRequiredError } from 'src/app/core/auth/reauth-error';
import { initiateInteractiveReauth } from 'src/app/core/auth/interactive-reauth';
import {
  ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY,
  ERROR_SCREEN_CAUSE_STATE_KEY,
} from 'src/app/core/domain/error-screen';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit {
  errorMessage: string | null = null;
  isEmailVerificationError: boolean = false;

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private auth0 = inject(AuthService);
  public translate = inject(TranslateService);
  private appInsightsService = inject(AppInsightsService);

  ngOnInit() {
    const supportedCodes = AVAILABLE_LANGUAGES.map(l => l.code);
    const browserLang = this.translate.getBrowserLang();
    const langToUse = supportedCodes.includes(browserLang) ? browserLang : 'en';

    this.translate.setDefaultLang('en');
    this.translate.use(langToUse);

    const params = this.activatedRoute.snapshot.queryParams;

    // 1. If there's no error, code, or state param, this likely means a refresh or direct visit to /callback
    if (!params['error'] && !params['code'] && !params['state']) {
      // Check if user is already authenticated
      this.auth0.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          // If they're logged in, send them to the app
          this.router.navigate(['/patients']); 
        } else {
          // If they're not logged in, send them to your home/login
          this.router.navigate(['/']);
        }
      });
      return;
    }

    // 2. If there's an error param, handle it
    if (params['error']) {
      console.error('Callback error:', params['error'], params['error_description']);
      if (params['error_description']?.includes('verify your email')) {
        console.log('Email verification error:', params['error_description']);
        this.errorMessage = params['error_description'];
        this.isEmailVerificationError = true;
        // Redirect to a dedicated page with replaceUrl to clear history
        this.router.navigate(['/verify-email'], { replaceUrl: true });
        // Clear query params so they're not reprocessed
        //this.router.navigate([], { queryParams: {} });
      } else if (params['error'] === 'access_denied') {
        // User cancelled OAuth flow (e.g., Google, passwordless) - redirect back to login
        console.log('Auth cancelled by user:', params['error_description']);
        this.appInsightsService.trackEvent('CallbackComponent::AuthCancelled', { error: params['error'], error_description: params['error_description'] });
        this.router.navigate(['/login'], { queryParams: { error: 'access_denied' }, replaceUrl: true });
      } else {
        this.appInsightsService.trackEvent('CallbackComponent::Auth0Error', { error: params['error'], error_description: params['error_description'] });
        // Spec 051 FR-004: hand the provider's machine-readable reason to the
        // 422 screen (via navigation state, NOT the URL) so it can record it.
        // The free-text `error_description` is deliberately never forwarded.
        this.router.navigate(['/page-error-422'], {
          state: {
            [ERROR_SCREEN_CAUSE_STATE_KEY]: 'auth',
            [ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY]: params['error'],
          },
        });
      }
      return;
    }

    // 3. Otherwise, code/state are present: handle the Auth0 redirect callback
    this.auth0.handleRedirectCallback().subscribe({
      next: (result) => {
        // Check for appState.target from landing page (deep link preservation)
        const targetRoute = result?.appState?.target;
        
        this.userService.user$.pipe(take(1)).subscribe((user) => {
          if (user && !user.privacy_policy_accepted) {
            this.router.navigate(['/files']);
          } else if (targetRoute && targetRoute !== '/') {
            // Redirect to the original destination if provided
            this.appInsightsService.trackEvent('deep_link_redirect', { targetRoute });
            this.router.navigate([targetRoute]);
          } else {
            this.router.navigate(['/patients']);
          }
        });
      },
      error: (err) => {
        // Redaction hygiene: log only an explicit safe subset, never the full
        // Auth0 error object (may carry tokens / PII-bearing error_description).
        console.error('Error handling redirect callback', {
          code: err?.error,
          name: (err as Error)?.name,
          message: (err as Error)?.message,
        });
        // A `missing_refresh_token` or `invalid_grant` ("Unknown or invalid
        // refresh token") during the callback = broken-but-authenticated
        // session; force a real interactive re-login (breaks the /login loop)
        // rather than routing to `/login`.
        if (isInteractiveReauthRequiredError(err)) {
          console.warn('[CallbackComponent] Broken refresh-token session, forcing interactive re-login...');
          initiateInteractiveReauth(this.auth0);
          return;
        }
        // A `login_required` error means the session expired during the OAuth
        // flow (unauthenticated) - redirect to the `/login` route.
        if (isLoginRequiredError(err)) {
          console.warn('[CallbackComponent] Login required, redirecting to login...');
          this.router.navigate(['/login'], { replaceUrl: true });
          return;
        }
        
        this.appInsightsService.trackEvent('CallbackComponent::ErrorHandlingRedirectCallback', {
          code: err?.error,
          name: (err as Error)?.name,
          message: (err as Error)?.message,
        });
        // Spec 051 FR-004: carry the machine-readable failure code to the 422
        // screen so the arrival is recorded as auth, not `unknown`.
        this.router.navigate(['/page-error-422'], {
          state: {
            [ERROR_SCREEN_CAUSE_STATE_KEY]: 'auth',
            [ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY]: err?.error,
          },
        });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/']);
  }
}